import React from 'react';
import styled from 'styled-components';

import Button from '../../buttons/Btn';
import { RecoveryPointType } from '../types';
import { BoldText } from '../styles';
import { formatDate, formatDateToDayTime } from '../helpers';
import { SELECTED_POINT } from '../constants';

const Container = styled.div`
  width: 516px;
  height: 100%;
  background-color: #fff;
  padding: 16px 24px;
  border-radius: 10px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    justify-content: flex-start;
  }
`;

const DataContainer = styled.div`
  display: flex;
  justify-content: space-between;
  background-color: #eef1f6;
  border-radius: 10px;
  padding: 8px 12px;
  align-items: center;
  gap: 10px;
  max-width: ${({ maxWidth }) => maxWidth || '100%'};

  @media (max-width: 768px) {
    width: fit-content;
    justify-content: center;
  }
`;

const PointsInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  margin-left: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 24px;
  align-items: center;
`;

const AvailabilityText = styled.span<{ color: string }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ color }) => color};
`;

const FullWidthButton = styled(Button)`
  width: calc(100% + 48px);
  margin-left: -24px;
  margin-right: -24px;
  margin-bottom: -24px;
  padding-top: 24px;

  background-color: #4a90e2;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  padding: 15px;
  border: none;
  border-radius: 0 0 10px 10px;
  cursor: pointer;
  margin-top: 16px;

  &:hover {
    background-color: #357abd;
  }
`;

const NextDate = styled.div`
  background-color: #f4f8fc;
  color: #4a90e2;
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 11px;
  width: fit-content;
  margin-top: 17px;
`;

type RecoveryPointProps = {
  point: RecoveryPointType;
  withAdditionalInfo?: boolean;
};

const RecoveryPoint = ({ point, withAdditionalInfo = true }: RecoveryPointProps) => {
  const { title, address, startDate, endDate, price, lowCapacity, name, nextStageDate, distance } =
    point;

  const isMobile = window.innerWidth <= 768;

  const formatAddress = (addr: string) => {
    const regex = /(\d{5}),\s*([A-Za-z]+)/;

    return addr.replace(regex, '$1 $2');
  };

  const handleBookingClick = () => {
    window.location.href = '/stage-recuperation-de-points/inscription/' + point.id;
  };

  const handleGoToPointClick = () => {
    window.location.href = `/stage-recuperation-de-points/${point.fullSlug}`;
  };

  return (
    <Container data-row-id={point.id}>
      {withAdditionalInfo && (
        <Header>
          <DataContainer maxWidth={isMobile && '290px'}>
            <BoldText fontWeight={700} fontSize={isMobile ? 14 : 18}>
              {formatDate(startDate)}
            </BoldText>
            <span>ET</span>
            <BoldText fontWeight={700} fontSize={isMobile ? 14 : 18}>
              {formatDate(endDate)}
            </BoldText>
          </DataContainer>
          <DataContainer maxWidth="90px">
            <BoldText fontSize={24} fontWeight={800}>
              {price} €
            </BoldText>
          </DataContainer>
        </Header>
      )}
      <PointsInfoContainer>
        <BoldText fontWeight={800}>{title || name}</BoldText>
        <span>
          {formatAddress(address)}
          {distance ? ` - ${distance} km` : ''}
        </span>
      </PointsInfoContainer>

      {nextStageDate && (
        <NextDate>
          Prochain stage le
          {formatDateToDayTime(nextStageDate)}
        </NextDate>
      )}
      {withAdditionalInfo ? (
        <ButtonContainer>
          <Button onClick={handleBookingClick} text="S’inscrire" />
          {lowCapacity ? (
            <AvailabilityText color="#f74656">Dernières places</AvailabilityText>
          ) : (
            <AvailabilityText color="#44c496">Places disponibles</AvailabilityText>
          )}
        </ButtonContainer>
      ) : (
        <FullWidthButton onClick={handleGoToPointClick} text="Je réserve un stage sur ce centre" />
      )}
    </Container>
  );
};

export { RecoveryPoint };
